import { fetchHandbooks, deleteHandbook as deleteHandbookApi, createHandbook, updateHandbook as updateHandbookApi, getHandbookDownloadUrl } from '@/apis/handbook';
import { formatTableDate } from '@/utils/dateFormatter';

const normalizeFileType = (type) => {
  const lookup = {
    uploaded: 'File',
    'external-url': 'Link',
  };
  const normalized = String(type || '').toLowerCase();
  return lookup[normalized] || 'File';
};

const formatUpdatedAt = (value) => {
  return formatTableDate(value);
};

const normalizeHandbookItems = (items = []) =>
  items.map((item) => ({
    id: item?.id,
    name: item?.title || item?.file_name || '--',
    type: normalizeFileType(item?.file_type),
    fileName: item?.file_name,
    fileUrl: item?.file_url,
    fileKey: item?.file_key,
    dateUploaded: formatUpdatedAt(item?.updated_at),
    visibilityType: item?.visibility_type || 'public',
    teamTagIds: item?.team_tag_ids || [],
    teamTagNames: item?.team_tag_names || [],
    teamUnitIds: item?.team_unit_ids || [],
    teamUnitNames: item?.team_unit_names || [],
    freelancerIds: item?.freelancer_ids || [],
    freelancerNames: item?.freelancer_names || [],
    raw: item,
  }));

export const handbookService = {
  getHandbooks: async ({ page = 1, pageSize = 10, search, team_id } = {}) => {
    try {
      const response = await fetchHandbooks({
        page,
        per_page: pageSize,
        search: search || undefined,
        team_id: team_id || undefined,
      });

      const payload = response?.data?.data || {};
      const list = normalizeHandbookItems(payload.items || []);

      return {
        success: true,
        data: {
          list,
          total: payload.total ?? list.length,
          pageSize: payload.per_page ?? pageSize,
          currentPage: payload.current_page ?? page,
          lastPage: payload.last_page ?? 1,
          hasMore: typeof payload.has_more === 'boolean' ? payload.has_more : false,
        },
      };
    } catch (error) {
      console.error('Failed to fetch handbooks:', error);
      return {
        success: false,
        error,
      };
    }
  },
  createHandbook: async (payload) => {
    try {
      const response = await createHandbook(payload);
      console.log('[handbookService] Create handbook response:', response);
      return { success: true, data: response?.data };
    } catch (error) {
      console.error('[handbookService] Failed to create handbook:', error);
      
      // Extract error message from different error formats
      let errorMessage = 'Upload failed, please try again.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.data?.message) {
        errorMessage = error.response.data.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      console.error('[handbookService] Error details:', {
        message: errorMessage,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      
      return { 
        success: false, 
        error: errorMessage,
        errorDetails: error 
      };
    }
  },
  updateHandbook: async (handbookId, payload) => {
    if (!handbookId) return { success: false, error: 'Handbook ID is required' };
    try {
      const response = await updateHandbookApi(handbookId, payload);
      console.log('[handbookService] Update handbook response:', response);
      return { success: true, data: response?.data };
    } catch (error) {
      console.error('[handbookService] Failed to update handbook:', error);
      
      // Extract error message from different error formats
      let errorMessage = 'Update failed, please try again.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.data?.message) {
        errorMessage = error.response.data.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      console.error('[handbookService] Error details:', {
        message: errorMessage,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      
      return { 
        success: false, 
        error: errorMessage,
        errorDetails: error 
      };
    }
  },
  deleteHandbook: async (handbookId) => {
    if (!handbookId) return { success: false };
    try {
      await deleteHandbookApi(handbookId);
      return { success: true };
    } catch (error) {
      console.error('Failed to delete handbook:', error);
      return { success: false, error };
    }
  },
  getDownloadUrl: async (handbookId, teamId) => {
    if (!handbookId) return { success: false, error: 'Handbook ID is required' };
    try {
      const params = teamId ? { team_id: teamId } : {};
      const response = await getHandbookDownloadUrl(handbookId, params);
      const data = response?.data?.data || {};
      const downloadUrl = data.download_url;
      const fileName = data.file_name;
      if (!downloadUrl) {
        return { success: false, error: 'Download URL not found' };
      }
      return { success: true, downloadUrl, fileName };
    } catch (error) {
      console.error('Failed to get download URL:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to get download URL';
      return { success: false, error: errorMessage };
    }
  },
};

export default handbookService;
